import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

export interface CollectionSheetData {
	debtorno: string;
	branchcode?: string;
	/** Pre-pin the note to one open document (from the per-invoice Log button). */
	transno?: number;
	trans_type?: number;
	/** Open straight into edit mode on an existing note. */
	note?: any;
	/**
	 * The worklist, in the order it was on screen. Lets the flyout act as a call
	 * sheet — work one account, hit Next, keep dialling — instead of forcing a
	 * close-and-reopen per customer.
	 */
	queue?: { debtorno: string; name?: string }[];
	/** Where in `queue` this account sits. */
	index?: number;
}

/**
 * Collections flyout — the fast path for working one account.
 *
 * Three things a collector needs before dialling, in the order they need them:
 * who touched this account last (so two people don't call the same customer an
 * hour apart), what money has landed since that call (a promise means nothing
 * without the deposit), and a one-screen form to log what just happened.
 *
 * Rendered as a plain fixed-position flyout — deliberately NOT a MatBottomSheet.
 * The app globally provides MAT_BOTTOM_SHEET_DEFAULT_OPTIONS with
 * `hasBackdrop: false` (components.module.ts), so a Material sheet can never dim
 * the page behind it, and the work surface never gets focus. Owning the overlay
 * here costs a wrapper div and removes the dependency.
 *
 * Emits `closed` with true when something was written, so the caller knows to
 * refresh.
 */
@Component({
	selector: 'app-collection-sheet',
	templateUrl: './collection-sheet.component.html',
	styleUrls: ['./collection-sheet.component.scss']
})
export class CollectionSheetComponent implements OnInit, OnDestroy {
	/** The scrolling body — its scrollTop is preserved across silent refreshes. */
	@ViewChild('scrollBox') scrollBox!: ElementRef<HTMLElement>;

	/** Spinner only on first open. Refreshes are silent so you keep your place. */
	loading = false;
	/** A refresh is in flight but the content stays on screen. */
	refreshing = false;
	saving = false;
	installed = true;
	installMessage = '';
	dirty = false;
	showAllFields = false;
	showAllNotes = false;

	customer: any = null;
	aging: any = null;
	openItems: any[] = [];
	payments: any[] = [];
	contacts: any[] = [];
	notes: any[] = [];

	/** Account currently shown — walks the queue without reopening the sheet. */
	debtorno = '';
	/** Branch for the account on screen. Cleared on navigate, never inherited. */
	branchcode = '';
	/** Documents this note is pinned to. A call often covers several invoices. */
	pinned: any[] = [];
	queueIndex = 0;

	form: UntypedFormGroup;
	editingId = 0;

	/** One tap sets outcome + a sensible follow-up. Order = how often they're used. */
	quickOutcomes = [
		{ value: 'promise', label: 'Promised to pay', icon: 'event', followupDays: 1 },
		{ value: 'no_answer', label: 'No answer', icon: 'phone', followupDays: 2 },
		{ value: 'message', label: 'Left message', icon: 'message', followupDays: 2 },
		{ value: 'partial', label: 'Partial promise', icon: 'call_split', followupDays: 1 },
		{ value: 'plan', label: 'Payment plan', icon: 'autorenew', followupDays: 7 },
		{ value: 'dispute', label: 'Disputes it', icon: 'report_problem', followupDays: 3 },
		{ value: 'paid', label: 'Says paid', icon: 'payment', followupDays: 3 },
		{ value: 'refused', label: 'Refused', icon: 'block', followupDays: 7 },
		{ value: 'escalated', label: 'Escalated', icon: 'trending_up', followupDays: 5 },
		{ value: 'other', label: 'Other', icon: 'more_horiz', followupDays: 0 },
	];

	contactTypes = [
		{ value: 'call', label: 'Call', icon: 'call' },
		{ value: 'voicemail', label: 'Voicemail', icon: 'message' },
		{ value: 'email', label: 'Email', icon: 'email' },
		{ value: 'letter', label: 'Letter', icon: 'mail' },
		{ value: 'portal', label: 'Portal', icon: 'language' },
		{ value: 'visit', label: 'Visit', icon: 'store' },
		{ value: 'note', label: 'Note', icon: 'notes' },
	];

	statuses = [
		{ value: 'open', label: 'Open', icon: 'schedule' },
		{ value: 'kept', label: 'Kept', icon: 'check_circle' },
		{ value: 'broken', label: 'Broken', icon: 'cancel' },
		{ value: 'closed', label: 'Closed', icon: 'archive' },
	];

	/** Everything the flyout needs to open on one account. */
	@Input() data: CollectionSheetData = { debtorno: '' };
	/** true when a note was written, so the caller refreshes. */
	@Output() closed = new EventEmitter<boolean>();

	constructor(
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
		private fb: UntypedFormBuilder,
	) {}

	ngOnInit(): void {
		// Stop the page behind from scrolling while the flyout owns the screen.
		document.body.classList.add('collection-flyout-open');
		this.debtorno = this.data.debtorno;
		this.branchcode = this.data.branchcode || '';
		this.queueIndex = typeof this.data.index === 'number' ? this.data.index : 0;
		this.form = this.fb.group({
			id: [0],
			debtorno: [this.data.debtorno, Validators.required],
			branchcode: [this.data.branchcode || ''],
			transno: [this.data.transno || 0],
			trans_type: [this.data.trans_type || 0],
			contact_date: [this.today, Validators.required],
			contact_type: ['call', Validators.required],
			outcome: ['promise', Validators.required],
			status: ['open', Validators.required],
			contact_name: [''],
			promise_date: [''],
			promise_amount: [0],
			followup_date: [''],
			note: ['', Validators.required],
		});
		if (this.data.note) {
			this.startEdit(this.data.note);
		}
		this.load();
	}

	get today(): string {
		return new Date().toISOString().substring(0, 10);
	}

	get overdue(): number {
		if (!this.aging) { return 0; }
		return Number(this.aging.b_1_30) + Number(this.aging.b_31_60)
			+ Number(this.aging.b_61_90) + Number(this.aging.b_90plus);
	}

	/**
	 * Brands carrying this customer's open balance, biggest first. The dollars are
	 * the server's prorated estimate — allocation sits on the document, not the
	 * line — so they answer "which brand is most of this?", not "bill this".
	 * Three on the header; the rest are counted, not listed.
	 */
	get brandMix(): Array<{ name: string, amount: number }> {
		if (!this.customer || !this.customer.brands) { return []; }
		const names = String(this.customer.brands).split('|');
		const amts = String(this.customer.brand_amts || '').split(',');
		return names.slice(0, 3).map((name, i) => ({ name: name, amount: Number(amts[i] || 0) }));
	}

	get brandMore(): number {
		if (!this.customer || !this.customer.brands) { return 0; }
		return Math.max(0, String(this.customer.brands).split('|').length - 3);
	}

	/** The note everyone else in the room should know about before they dial. */
	get lastNote(): any {
		return this.notes.length ? this.notes[0] : null;
	}

	get visibleNotes(): any[] {
		return this.showAllNotes ? this.notes : this.notes.slice(0, 4);
	}

	/**
	 * Hours since the last contact was keyed — drives the collision warning.
	 * Comes from the server's `age_seconds`, because the DB clock and the user's
	 * clock sit in different zones and a four-hour skew would make this lie.
	 */
	get hoursSinceLastContact(): number | null {
		const n = this.lastNote;
		if (!n || n.age_seconds === null || n.age_seconds === undefined) { return null; }
		return Math.max(0, Number(n.age_seconds) / 3600);
	}

	/** Somebody already worked this account very recently — say so loudly. */
	get collision(): boolean {
		const h = this.hoursSinceLastContact;
		return h !== null && h < 24;
	}

	get lastContactAgo(): string {
		const h = this.hoursSinceLastContact;
		if (h === null) { return ''; }
		const mins = Math.round(h * 60);
		if (mins < 2) { return 'just now'; }
		if (h < 1) { return mins + 'm ago'; }
		if (h < 24) { return Math.round(h) + 'h ago'; }
		return Math.round(h / 24) + 'd ago';
	}

	/** Money that landed since the newest note was written. */
	get paidSinceLastNote(): number {
		const n = this.lastNote;
		if (!n) { return 0; }
		const then = Number(n.balance_at_note || 0);
		const now = this.aging ? Number(this.aging.balance || 0) : 0;
		return Math.max(0, then - now);
	}

	/** Receipts/credits dated on or after the newest note. */
	get paymentsSinceLastNote(): any[] {
		const n = this.lastNote;
		if (!n || !n.contact_date) { return []; }
		const since = String(n.contact_date).substring(0, 10);
		return this.payments.filter(p => String(p.trandate).substring(0, 10) >= since);
	}

	/** An open promise whose date has come and gone. */
	/**
	 * Broken either way: explicitly marked broken, or still open with a promised
	 * date that has passed. Only checking the second was hiding the ones a human
	 * had already judged.
	 */
	get brokenPromises(): any[] {
		return this.notes.filter(n =>
			n.status === 'broken'
			|| (n.status === 'open' && n.promise_date && n.promise_date < this.today)
		);
	}

	/**
	 * Pull the account. `silent` keeps the current content and scroll position on
	 * screen while the request runs — saving a note must not throw you back to
	 * the top of the flyout.
	 */
	load(silent = false) {
		// Hold the scroll position: even with no spinner, re-assigning the arrays
		// re-renders the lists, and a momentary height collapse makes the browser
		// clamp scrollTop. trackBy keeps the rows, this keeps the offset.
		const box = this.scrollBox ? this.scrollBox.nativeElement : null;
		const keepScroll = (silent && box) ? box.scrollTop : null;

		if (silent) {
			this.refreshing = true;
		} else {
			this.loading = true;
		}
		this.paymentsService.getCollectionCustomer(this.debtorno).subscribe((r: any) => {
			this.loading = false;
			this.refreshing = false;
			if (keepScroll !== null && box) {
				setTimeout(() => { box.scrollTop = keepScroll; });
			}
			if (!r || !r.success) {
				this.globalSearchService.showNotification(
					(r && r.message) || 'Could not load collections', 'danger', 'bottom', 'left'
				);
				return;
			}
			this.installed = !!r.installed;
			this.installMessage = r.message || '';
			this.customer = r.customer || null;
			this.aging = r.aging || null;
			this.openItems = r.open_items || [];
			this.payments = r.payments || [];
			this.contacts = r.contacts || [];
			this.notes = r.notes || [];
			if (!this.editingId && !this.form.get('contact_name').value && this.customer) {
				this.form.get('contact_name').setValue(this.customer.contactname || '');
			}
		});
	}

	/** Chip tap: set the outcome and pre-fill a follow-up date that suits it. */
	pickOutcome(o: any) {
		this.form.get('outcome').setValue(o.value);
		if (o.followupDays > 0 && !this.form.get('followup_date').value) {
			const d = new Date();
			d.setDate(d.getDate() + o.followupDays);
			this.form.get('followup_date').setValue(d.toISOString().substring(0, 10));
		}
		if (o.value === 'promise' && !Number(this.form.get('promise_amount').value)) {
			this.form.get('promise_amount').setValue(this.overdue > 0 ? this.overdue : (this.aging ? this.aging.balance : 0));
		}
	}

	isOutcome(value: string): boolean {
		return this.form && this.form.get('outcome').value === value;
	}

	/** Toggle a document in or out of the pin set. */
	togglePin(item: any) {
		const at = this.pinned.findIndex(p => Number(p.transno) === Number(item.transno));
		if (at >= 0) {
			this.pinned.splice(at, 1);
			return;
		}
		this.pinned.push(item);
		this.branchcode = item.branchcode;
		// Seed the promise with the total of what's pinned — that's usually the ask.
		this.form.get('promise_amount').setValue(this.pinnedTotal);
	}

	isPinned(item: any): boolean {
		return this.pinned.some(p => Number(p.transno) === Number(item.transno));
	}

	unpin(item: any) {
		this.pinned = this.pinned.filter(p => Number(p.transno) !== Number(item.transno));
	}

	get pinnedTotal(): number {
		return this.pinned.reduce((a, p) => a + Number(p.balance || 0), 0);
	}

	/** Pin every open document at once — "the whole account" is a common case. */
	pinAll() {
		this.pinned = this.openItems.slice();
		this.form.get('promise_amount').setValue(this.pinnedTotal);
	}

	clearPins() {
		this.pinned = [];
	}

	/**
	 * "Invoice 1891385" rather than a naked number. The server sends `typename`
	 * from systypenames; the map is the fallback for a pin whose document has been
	 * purged, so the row still says what kind of thing it was.
	 */
	docLabel(pin: any): string {
		if (pin && pin.typename) { return pin.typename; }
		const t = Number(pin && pin.trans_type);
		if (t === 10) { return 'Invoice'; }
		if (t === 11) { return 'Credit'; }
		if (t === 12) { return 'Receipt'; }
		return 'Doc';
	}

	startEdit(note: any) {
		this.editingId = note.id;
		this.showAllFields = true;
		// Rebuild the pin set from the note's saved documents.
		//
		// `openItems` only carries what is still OPEN, so filtering the pins through
		// it silently dropped every pin on a document that has since settled — which
		// is most of an imported back-log, and made the form look like nothing was
		// pinned at all. Walk the note's own doc list instead and keep the ones the
		// open list can't explain, carrying their number and saying why they have no
		// balance.
		const pins = (note.doc_pins && note.doc_pins.length)
			? note.doc_pins
			: (note.docs || []).map((d: any) => ({ transno: d, trans_type: note.trans_type }));
		const docs = pins.length
			? pins
			: (note.transno ? [{ transno: note.transno, trans_type: note.trans_type }] : []);
		this.pinned = docs.map((d: any) => {
			const open = this.openItems.find(i => Number(i.transno) === Number(d.transno));
			if (open) { return open; }
			// `type`, not `trans_type` — that's the key save() reads back off a pin.
			return {
				transno: Number(d.transno),
				type: Number(d.trans_type) || 0,
				typename: d.typename || this.docLabel(d),
				reference: d.reference || '',
				balance: d.balance,
				settled: d.settled === undefined ? 1 : d.settled,
			};
		});
		this.form.patchValue({
			id: note.id,
			debtorno: note.debtorno,
			branchcode: note.branchcode,
			transno: note.transno,
			trans_type: note.trans_type,
			contact_date: (note.contact_date || '').substring(0, 10),
			contact_type: note.contact_type,
			outcome: note.outcome,
			status: note.status,
			contact_name: note.contact_name,
			promise_date: (note.promise_date || '').substring(0, 10),
			promise_amount: note.promise_amount,
			followup_date: (note.followup_date || '').substring(0, 10),
			note: note.note,
		});
	}

	cancelEdit() {
		this.editingId = 0;
		this.pinned = [];
		this.form.reset({
			id: 0,
			debtorno: this.debtorno,
			branchcode: this.branchcode,
			transno: 0,
			trans_type: 0,
			contact_date: this.today,
			contact_type: 'call',
			outcome: 'promise',
			status: 'open',
			contact_name: this.customer ? this.customer.contactname : '',
			promise_date: '',
			promise_amount: 0,
			followup_date: '',
			note: '',
		});
	}

	save() {
		if (!this.form.valid || this.saving) { return; }
		this.saving = true;
		const payload = Object.assign({}, this.form.value, {
			transnos: this.pinned.map(p => ({ transno: p.transno, trans_type: p.type })),
		});
		this.paymentsService.saveCollectionNote(payload).subscribe((r: any) => {
			this.saving = false;
			if (r && r.success) {
				this.dirty = true;
				this.globalSearchService.showNotification('Contact logged', 'success', 'bottom', 'left');
				this.cancelEdit();
				this.load(true);
			} else {
				this.globalSearchService.showNotification((r && r.message) || 'Save failed', 'danger', 'bottom', 'left');
			}
		});
	}

	/** Close out a promise from the list without reopening the form. */
	setStatus(note: any, status: string) {
		this.paymentsService.saveCollectionNote(Object.assign({}, note, { status })).subscribe((r: any) => {
			if (r && r.success) {
				this.dirty = true;
				this.load(true);
			}
		});
	}

	remove(note: any) {
		if (!confirm('Delete this collection note?')) { return; }
		this.paymentsService.removeCollectionNote(note.id).subscribe((r: any) => {
			if (r && r.success) {
				this.dirty = true;
				this.load(true);
			}
		});
	}

	// -- stable list identity, so a silent refresh doesn't rebuild the DOM ----

	trackNote(index: number, note: any) {
		return note.id;
	}

	trackDoc(index: number, doc: any) {
		return doc.transno;
	}

	trackContact(index: number, c: any) {
		return (c.phone || '') + '|' + (c.email || '');
	}

	close() {
		this.closed.emit(this.dirty);
	}

	/** Escape closes, matching every other dismissible surface in the app. */
	@HostListener('document:keydown.escape')
	onEscape() {
		this.close();
	}

	/** Click on the dimmed area closes; clicks inside the panel must not. */
	onBackdropClick() {
		this.close();
	}

	ngOnDestroy(): void {
		document.body.classList.remove('collection-flyout-open');
	}

	// -- call sheet -------------------------------------------------------

	/** `tel:` needs digits and a leading +, not the spaces people type. */
	telHref(phone: string): string {
		return 'tel:' + this.digits(phone);
	}

	/**
	 * `mailto:` with the account and what's owed already in the subject, so the
	 * collector isn't retyping it. This only opens their mail client with a
	 * draft — nothing is sent until they send it.
	 */
	mailHref(email: string): string {
		const who = this.customer ? this.customer.name : this.debtorno;
		const subject = 'Past due account ' + this.debtorno + ' — ' + who;
		const owed = this.overdue > 0 ? this.overdue : (this.aging ? Number(this.aging.balance || 0) : 0);
		const body = 'Our records show ' + owed.toFixed(2) + ' past due on account ' + this.debtorno + '.';
		return 'mailto:' + email
			+ '?subject=' + encodeURIComponent(subject)
			+ '&body=' + encodeURIComponent(body);
	}

	/**
	 * KM (and others) sometimes park a numeric code in `contactname` and put the
	 * real signal in `role` — show whichever actually reads as a label.
	 */
	contactLabel(c: any): string {
		const name = String(c.name || '').trim();
		const role = String(c.role || '').trim();
		const named = name !== '' && !/^\d+$/.test(name);
		if (named && role) { return name + ' · ' + role; }
		if (named) { return name; }
		return role || 'Contact';
	}

	/**
	 * Phones first — a call sheet is for dialling.
	 *
	 * Deduped per channel, not per row: one contact row can carry both a number
	 * and an address that another row already supplies, so filtering the shared
	 * list would print the same address twice.
	 */
	get phoneContacts(): any[] {
		return this.uniqueBy(this.contacts.filter(c => c.phone), c => this.digits(c.phone));
	}

	get emailContacts(): any[] {
		return this.uniqueBy(this.contacts.filter(c => c.email), c => String(c.email).toLowerCase());
	}

	private uniqueBy(rows: any[], key: (row: any) => string): any[] {
		const seen = new Set<string>();
		return rows.filter(r => {
			const k = key(r);
			if (seen.has(k)) { return false; }
			seen.add(k);
			return true;
		});
	}

	private digits(phone: string): string {
		return String(phone || '').replace(/[^\d+]/g, '');
	}

	copy(value: string) {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
			this.globalSearchService.showNotification('Copied ' + value, 'success', 'bottom', 'left');
		}
	}

	// -- running down the list --------------------------------------------

	get queue(): { debtorno: string; name?: string }[] {
		return this.data.queue || [];
	}

	get hasQueue(): boolean {
		return this.queue.length > 1;
	}

	get queuePosition(): string {
		return (this.queueIndex + 1) + ' of ' + this.queue.length;
	}

	get nextInQueue(): any {
		return this.queue[this.queueIndex + 1] || null;
	}

	get prevInQueue(): any {
		return this.queueIndex > 0 ? this.queue[this.queueIndex - 1] : null;
	}

	/** Move to another account in place — the sheet stays open. */
	go(step: number) {
		const target = this.queue[this.queueIndex + step];
		if (!target) { return; }
		this.queueIndex += step;
		this.debtorno = target.debtorno;
		this.branchcode = '';
		this.pinned = [];
		this.editingId = 0;
		this.showAllNotes = false;
		this.form.patchValue({
			id: 0,
			debtorno: target.debtorno,
			branchcode: '',
			transno: 0,
			trans_type: 0,
			contact_name: '',
			note: '',
			promise_amount: 0,
			promise_date: '',
			followup_date: '',
			outcome: 'promise',
			status: 'open',
		});
		this.load();
	}

	/** Trailing "· broken" / "· kept" on the last-contact line, when it applies. */
	get lastStatusSuffix(): string {
		const n = this.lastNote;
		if (!n) { return ''; }
		if (n.status === 'broken') { return ' · broken'; }
		if (n.status === 'kept') { return ' · kept'; }
		if (n.status === 'closed') { return ' · closed'; }
		if (n.promise_date && n.promise_date < this.today) { return ' · promise broken'; }
		return '';
	}

	statusPill(note: any): 'settled' | 'partial' | 'failed' | 'neutral' {
		if (note.status === 'open' && note.promise_date && note.promise_date < this.today) { return 'failed'; }
		switch (note.status) {
		case 'kept': return 'settled';
		case 'broken': return 'failed';
		case 'closed': return 'neutral';
		default: return 'partial';
		}
	}

	statusLabel(note: any): string {
		if (note.status === 'open' && note.promise_date && note.promise_date < this.today) { return 'promise broken'; }
		return note.status;
	}

	outcomeLabel(value: string): string {
		const hit = this.quickOutcomes.find(o => o.value === value);
		return hit ? hit.label : value;
	}

	outcomeIcon(value: string): string {
		const hit = this.quickOutcomes.find(o => o.value === value);
		return hit ? hit.icon : 'chat_bubble_outline';
	}

	pickContactType(value: string) {
		this.form.get('contact_type').setValue(value);
	}

	/**
	 * Tapping a phone or email on the call sheet says "this is how I reached
	 * them" — it sets the contact type and borrows the name, so the note lands
	 * as an Email instead of silently defaulting to Call. The mail client only
	 * opens from the small launch icon beside it.
	 */
	useContact(c: any, type: string) {
		this.pickContactType(type);

		const who = String((c && c.name) || '').trim();
		const nameCtl = this.form.get('contact_name');
		if (who && who !== String(nameCtl.value || '').trim()) {
			nameCtl.setValue(who);
		}

		const box = document.getElementById('cs-note');
		if (box) { box.focus(); }
	}

	isContactType(value: string): boolean {
		return this.form && this.form.get('contact_type').value === value;
	}

	pickStatus(value: string) {
		this.form.get('status').setValue(value);
	}

	isStatus(value: string): boolean {
		return this.form && this.form.get('status').value === value;
	}

	contactTypeLabel(value: string): string {
		const hit = this.contactTypes.find(o => o.value === value);
		return hit ? hit.label : value;
	}
}
